import { ForbiddenCodeEnum } from '@/context/services/type';
import { extractInvoiceReportErrorMessage } from '@/features/invoice_reports/utils/extractErrorMessage';
import { useTranslation } from '@/i18n';
import { toHtmlNode } from '@/utils/toHtmlNode';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import { useCallback, useRef } from 'react';

const AGENT_ENTRY_NEW_PATH = '/agent_entry/new';
const TOAST_DURATION = 5;

/** Host document when the app runs in an iframe (Rails shell is parent). */
function hostDocument(): Document {
  return typeof window.parent !== 'undefined' && window.parent !== window
    ? window.parent.document
    : document;
}

function agentEntryNewUrl(reportTypeKey: AgentEntryOpenDetail): string {
  const q = new URLSearchParams({ report_type_key: reportTypeKey });
  return `${AGENT_ENTRY_NEW_PATH}?${q.toString()}`;
}

function absoluteUrl(href: string, doc: Document): string {
  const base = doc.defaultView?.location ?? window.location;
  return new URL(href, base.href).href;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- legacy jQuery + plugins (modal, publish)
type JQueryStatic = any;

function isJQueryStatic(value: unknown): value is JQueryStatic {
  return typeof value === 'function';
}

/** jQuery from the host window (legacy bootstrap modal + pubsub). */
function jquery(doc: Document): JQueryStatic | undefined {
  const w = doc.defaultView;
  if (!w) return undefined;
  const $ = Reflect.get(w, 'jQuery');
  return isJQueryStatic($) ? $ : undefined;
}

/** Legacy shell loading overlay (same as jQuery ajax beforeSend / complete). */
function publishExLoading(doc: Document, action: 'start' | 'hide'): void {
  const $ = jquery(doc);
  $?.publish?.(action === 'start' ? 'ex:loading:start' : 'ex:loading:hide');
}

/**
 * Same as ap_web ExRemoteModal success: one `div.modal` from HTML, append, show, pubsub for agent_entry_reports init.
 */
function showAgentEntryModalFromHtml(doc: Document, html: string): void {
  const $ = jquery(doc);
  if (!$) {
    location.reload();
    return;
  }

  const $modal = $(html).filter('div.modal');
  if ($modal.length !== 1) {
    // eslint-disable-next-line no-console
    console.error(
      `agent entry modal: expected one div.modal, got ${$modal.length}`
    );
    location.reload();
    return;
  }

  $modal.appendTo(doc.body);
  $modal.one('shown.bs.modal', () => {
    $.publish?.('ex:remote_modal:show', $modal);
  });
  $modal.one('hidden.bs.modal', () => {
    $modal.remove();
    $.publish?.('ex:remote_modal:hide');
  });
  $modal.modal('show');
}

export enum AgentEntryOpenDetail {
  InvoiceReport = 'invoice_report',
  InvoiceReportBulkUpload = 'invoice_report_bulk_upload',
}

type AgentEntry403Json = { code?: string; messages?: string[] };

/** Parse 403 JSON without type assertions (server may omit fields). */
function parseAgentEntry403Body(text: string): AgentEntry403Json | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;

  const result: AgentEntry403Json = {};
  if ('code' in parsed) {
    const code = Reflect.get(parsed, 'code');
    if (typeof code === 'string') result.code = code;
  }
  if ('messages' in parsed) {
    const messages = Reflect.get(parsed, 'messages');
    if (
      Array.isArray(messages) &&
      messages.every((m): m is string => typeof m === 'string')
    ) {
      result.messages = messages;
    }
  }
  return result;
}

/**
 * GET new agent entry HTML (XHR headers → JSON 403), inject bootstrap modal, toast on FORBIDDEN_MIMIC_MODE.
 */
export function useOpenAgentEntryModal(): (
  reportTypeKey: AgentEntryOpenDetail
) => void {
  const controllerRef = useRef<AbortController | null>(null);
  /** Prevents `ex:loading:hide` from a superseded/aborted fetch from clearing the overlay while a newer request runs. */
  const loadingGenerationRef = useRef(0);
  const notify = useToastAPI();
  const { t } = useTranslation();

  return useCallback(
    (reportTypeKey: AgentEntryOpenDetail) => {
      const doc = hostDocument();
      const url = absoluteUrl(agentEntryNewUrl(reportTypeKey), doc);

      controllerRef.current?.abort();
      const ac = new AbortController();
      controllerRef.current = ac;

      const generation = ++loadingGenerationRef.current;
      publishExLoading(doc, 'start');

      void (async () => {
        try {
          const response = await fetch(url, {
            method: 'GET',
            credentials: 'same-origin',
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
              Accept: 'text/html,application/xhtml+xml',
            },
            signal: ac.signal,
          });

          if (response.ok) {
            showAgentEntryModalFromHtml(doc, await response.text());
            return;
          }

          if (response.status === 403) {
            const body = parseAgentEntry403Body(await response.text());
            if (body?.code === ForbiddenCodeEnum.FORBIDDEN_MIMIC_MODE) {
              const title =
                extractInvoiceReportErrorMessage(
                  { code: body.code, messages: body.messages },
                  t
                ) ?? t('invoice_reports.error.forbidden_mimic_mode');
              notify.error({
                title: toHtmlNode(title ?? ''),
                duration: TOAST_DURATION,
              });
              return;
            }
            if (body?.messages?.length) {
              notify.error({
                title: toHtmlNode(body.messages.join('\n') ?? ''),
                duration: TOAST_DURATION,
              });
              return;
            }

            location.reload();
            return;
          }

          location.reload();
        } catch (e) {
          if (e instanceof DOMException && e.name === 'AbortError') return;
          location.reload();
        } finally {
          if (loadingGenerationRef.current === generation) {
            publishExLoading(doc, 'hide');
          }
        }
      })();
    },
    [notify, t]
  );
}
